import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Message, toaster } from 'rsuite';
import styled from 'styled-components';
import useSWR from 'swr';
import axios from 'axios';

import ScheduleCoordinationTable from './ScheduleCoordinationTable';
import AddScheduleModal from './AddScheduleModal';

import 'rsuite/Modal/styles/index.css';
import 'rsuite/Button/styles/index.css';
import 'rsuite/SelectPicker/styles/index.css';

const CoordinationWrapper = styled.div`
    background-color: white;
    border-radius: 8px;
    padding: 20px;
    position: relative;
`;

const Header = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
    padding-bottom: 15px;
    border-bottom: 1px solid #e5e5e5;
    
    .title {
        font-size: 20px;
        font-weight: bold;
    }
    
    .status-badge {
        display: inline-block;
        padding: 4px 12px;
        border-radius: 12px;
        font-size: 12px;
        font-weight: bold;
        margin-left: 10px;
    }
    
    .status-locked {
        background-color: #ff9800;
        color: white;
    }
    
    .status-editable {
        background-color: #4caf50;
        color: white;
    }
`;

const ScheduleCoordination = () => {
    const { data: enabledRes } = useSWR(`/api/work-schedule/enabled`, axios);
    const { data: typesRes } = useSWR(`/api/work-schedule-types`, axios);
    const { data: usersRes } = useSWR(`/api/work-schedule/users`, axios);
    
    const [nowDate, setNowDate] = useState(() => {
        // 計算下個月的日期
        const currentDate = new Date();
        const nextMonth = currentDate.getMonth() + 1;
        const nextYear = currentDate.getFullYear();
        return new Date(nextYear, nextMonth, 1);
    });
    
    const [loading, setLoading] = useState(false);
    const [draftSchedules, setDraftSchedules] = useState({});
    const [showAddModal, setShowAddModal] = useState(false);
    const [selectedDate, setSelectedDate] = useState(null);
    const [selectedTypeId, setSelectedTypeId] = useState(null);
    const [isLocked, setIsLocked] = useState(false);
    const [currentUser, setCurrentUser] = useState(null);
    
    // 從 window 獲取當前用戶信息
    useEffect(() => {
        if (window.currentUser) {
            setCurrentUser(window.currentUser);
            console.log('Current user loaded:', window.currentUser);
        } else {
            console.warn('window.currentUser is not set');
        }
    }, []);

    const isEnabled = useMemo(() => enabledRes?.data?.enabled || false, [enabledRes]);
    const scheduleTypes = useMemo(() => typesRes?.data?.types || [], [typesRes]);
    const users = useMemo(() => usersRes?.data || [], [usersRes]);
    
    const year = nowDate.getFullYear();
    const month = nowDate.getMonth() + 1;

    // 獲取草稿排班數據
    const { data: draftRes, mutate: mutateDrafts } = useSWR(
        isEnabled ? `/api/work-schedule-draft/month?year=${year}&month=${month}` : null,
        axios
    );

    // 處理草稿數據
    useEffect(() => {
        if (draftRes?.data) {
            // API 返回格式：{ schedules: {...}, is_locked: true/false }
            const schedules = draftRes.data.schedules || {};
            setDraftSchedules(schedules);
            setIsLocked(draftRes.data.is_locked || false);
        } else {
            setDraftSchedules({});
            setIsLocked(false);
        }
    }, [draftRes]);

    // 根據類型 ID 獲取顏色
    const getColorForType = useCallback((typeId) => {
        const type = scheduleTypes.find(t => parseInt(t.id) === parseInt(typeId));
        return type?.color || '#f0f0f0';
    }, [scheduleTypes]);

    // 格式化日期
    const formatDate = useCallback((date) => {
        if (!date) return '';
        const dateObj = date instanceof Date ? date : new Date(date);
        if (isNaN(dateObj.getTime())) return '';
        const year = dateObj.getFullYear();
        const month = (dateObj.getMonth() + 1).toString().padStart(2, '0');
        const day = dateObj.getDate().toString().padStart(2, '0');
        return `${year}-${month}-${day}`;
    }, []);

    // 處理日期雙擊
    const handleDateDoubleClick = useCallback((date, userUuid) => {
        console.log('handleDateDoubleClick called', { isLocked, currentUser, userUuid, date });
        
        if (isLocked) {
            console.log('頁面已鎖定，無法編輯');
            toaster.push(<Message type="warning">該月份已鎖定，無法編輯</Message>, { placement: 'topCenter' });
            return;
        }
        
        // 只能編輯自己的排班
        const user = currentUser || window.currentUser;
        if (!user) {
            console.log('currentUser 為空');
            toaster.push(<Message type="warning">無法識別當前用戶，請重新整理頁面</Message>, { placement: 'topCenter' });
            return;
        }
        
        if (userUuid !== user.uuid) {
            console.log('只能編輯自己的排班', { userUuid, currentUserUuid: user.uuid });
            toaster.push(<Message type="info">只能編輯自己的排班</Message>, { placement: 'topCenter' });
            return;
        }
        
        console.log('開啟新增排班視窗');
        setSelectedDate(date);
        setSelectedTypeId(null);
        setShowAddModal(true);
    }, [isLocked, currentUser]);

    // 處理班別選擇
    const handleTypeSelect = useCallback(async (typeId) => {
        if (!selectedDate) return;
        
        const user = currentUser || window.currentUser;
        if (!user) {
            toaster.push(<Message type="error">無法識別當前用戶</Message>, { placement: 'topCenter' });
            return;
        }
        
        setLoading(true);
        try {
            await axios.post('/api/work-schedule-draft/store', {
                date: formatDate(selectedDate),
                user_uuid: user.uuid,
                type_id: typeId,
                is_national_holiday: 0,
                year: year,
                month: month
            });
            
            // 重新獲取數據
            await mutateDrafts();
            toaster.push(<Message type="success">排班已新增</Message>, { placement: 'topCenter' });
            setShowAddModal(false);
            setSelectedDate(null);
            setSelectedTypeId(null);
        } catch (err) {
            console.error('新增排班失敗:', err);
            const errorMsg = err.response?.data?.message || '新增排班失敗';
            toaster.push(<Message type="error">{errorMsg}</Message>, { placement: 'topCenter' });
        } finally {
            setLoading(false);
        }
    }, [selectedDate, currentUser, year, month, formatDate, mutateDrafts]);

    // 處理刪除排班
    const handleRemoveSchedule = useCallback(async (scheduleId) => {
        if (isLocked) return;
        
        if (!confirm('確定要刪除此排班嗎？')) return;
        
        setLoading(true);
        try {
            await axios.delete('/api/work-schedule-draft/destroy', {
                data: {
                    schedule_id: scheduleId,
                    _token: document.querySelector('meta[name="csrf-token"]')?.content
                }
            });
            
            await mutateDrafts();
            toaster.push(<Message type="success">排班已刪除</Message>, { placement: 'topCenter' });
        } catch (err) {
            console.error('刪除排班失敗:', err);
            const errorMsg = err.response?.data?.message || '刪除排班失敗';
            toaster.push(<Message type="error">{errorMsg}</Message>, { placement: 'topCenter' });
        } finally {
            setLoading(false);
        }
    }, [isLocked, mutateDrafts]);

    // 調試信息 - 必須在所有 Hooks 之後，早期返回之前
    useEffect(() => {
        console.log('ScheduleCoordination 狀態:', {
            currentUser,
            isLocked,
            usersCount: users.length,
            scheduleTypesCount: scheduleTypes.length,
            draftSchedulesCount: Object.keys(draftSchedules).length
        });
    }, [currentUser, isLocked, users.length, scheduleTypes.length, Object.keys(draftSchedules).length]);

    if (!isEnabled) {
        return (
            <div className="alert alert-info">
                <h4>班表系統尚未啟用</h4>
                <p>請聯絡系統管理員在「設定系統參數」中啟用班表系統。</p>
            </div>
        );
    }

    const monthName = `${year}年${month}月`;

    return (
        <CoordinationWrapper>
            <Header>
                <div>
                    <span className="title">排班協調 - {monthName}</span>
                    {isLocked ? (
                        <span className="status-badge status-locked">已鎖定</span>
                    ) : (
                        <span className="status-badge status-editable">可編輯</span>
                    )}
                    {currentUser && (
                        <span style={{ marginLeft: '10px', fontSize: '12px', color: '#666' }}>
                            (當前用戶: {currentUser.name})
                        </span>
                    )}
                </div>
            </Header>

            {isLocked && (
                <div style={{
                    backgroundColor: '#e3f2fd',
                    color: '#1976d2',
                    padding: '12px 16px',
                    borderRadius: '4px',
                    marginBottom: '20px',
                    fontSize: '14px'
                }}>
                    <strong>注意：</strong>此月份的排班已經管理員審核並鎖定，無法再進行編輯。
                </div>
            )}

            {!isLocked && (
                <div style={{
                    backgroundColor: '#fff3cd',
                    color: '#856404',
                    padding: '12px 16px',
                    borderRadius: '4px',
                    marginBottom: '20px',
                    fontSize: '14px'
                }}>
                    <strong>說明：</strong>請在此頁面安排您下個月（{monthName}）的排班與休假。雙擊日期即可新增排班。
                </div>
            )}

            <ScheduleCoordinationTable
                nowDate={nowDate}
                users={users}
                draftSchedules={draftSchedules}
                scheduleTypes={scheduleTypes}
                currentUser={currentUser}
                isLocked={isLocked}
                getColorForType={getColorForType}
                onDateDoubleClick={handleDateDoubleClick}
                onRemoveSchedule={handleRemoveSchedule}
                formatDate={formatDate}
            />

            {/* 圖例說明 */}
            <div style={{
                marginTop: '20px',
                padding: '12px',
                backgroundColor: '#f8f9fa',
                borderRadius: '4px',
                border: '1px solid #e5e5e5'
            }}>
                <h4 style={{ marginTop: 0, marginBottom: '10px', fontSize: '14px', fontWeight: 'bold' }}>班別說明</h4>
                <div style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '15px'
                }}>
                    {scheduleTypes.map((type) => {
                        const dayType = type.day_type || 'normal';
                        // 根據日期類型決定顏色
                        let typeColor = type.color;
                        if (dayType === 'rest_day') {
                            typeColor = '#ff9800'; // 休息日：橙色
                        } else if (dayType === 'regular_holiday') {
                            typeColor = '#f44336'; // 例假日：紅色
                        }
                        
                        let dayTypeLabel = '';
                        if (dayType === 'rest_day') {
                            dayTypeLabel = ' [休息日]';
                        } else if (dayType === 'regular_holiday') {
                            dayTypeLabel = ' [例假日]';
                        }
                        
                        const timeRange = type.time_range || '';
                        
                        return (
                            <div key={type.id} style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px',
                                fontSize: '12px'
                            }}>
                                <span style={{
                                    width: '14px',
                                    height: '14px',
                                    borderRadius: '50%',
                                    backgroundColor: typeColor,
                                    border: '1px solid rgba(0,0,0,0.1)',
                                    flexShrink: 0,
                                    display: 'inline-block'
                                }}></span>
                                <span>{type.name}{dayTypeLabel}{timeRange ? `：${timeRange}` : ''}</span>
                            </div>
                        );
                    })}
                </div>
            </div>

            <AddScheduleModal
                open={showAddModal}
                onClose={() => {
                    setShowAddModal(false);
                    setSelectedDate(null);
                    setSelectedTypeId(null);
                }}
                selectedDate={selectedDate}
                scheduleTypes={scheduleTypes}
                onTypeSelect={handleTypeSelect}
                loading={loading}
            />

            {loading && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(255, 255, 255, 0.8)',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    zIndex: 1000
                }}>
                    <div style={{ padding: '20px', backgroundColor: 'white', borderRadius: '8px', boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)' }}>
                        處理中...
                    </div>
                </div>
            )}
        </CoordinationWrapper>
    );
};

export default ScheduleCoordination;
